import { Modal, Input, Empty, Table, Tag, Button, message } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePosPromotionList, usePosVoucherList, usePosValidateVoucher, usePosLoyaltyTiers } from '@/hooks/usePromotions';
import {
  PROMOTION_TYPE_LABEL,
  PROMOTION_STATUS_LABEL,
  PROMOTION_STATUS_COLOR,
  type PromotionType,
  type PromotionStatus,
} from '@/mocks/promotions';
import { formatVND, formatDate } from '@/utils/format';
import { posService } from '@/services/pos.service';
import { mapDetailToPosVariant } from '../../utils/mapVariantDetail';
import { buildGiftOrderItem } from '../../utils/giftItems';
import { buildEvaluateItems } from '../../utils/buildEvaluateItems';
import { GiftVariantPickerDialog } from '../GiftVariantPickerDialog';
import { useGiftVariantPicker } from '../../hooks/useGiftVariantPicker';
import type { OrderItem, FulfillmentMode, PosGiftVariantOption } from '../../types';
import type {
  ValidateVoucherPayload,
  VoucherValidationResult,
  PromotionCartEvaluationDto,
} from '@/services/promotion.service';
import { useCustomerGroups } from '@/hooks/useCustomers';
import './VoucherModal.css';

export interface VoucherItem {
  code: string;
  label: string;
  description?: string;
  /** Số tiền giảm do backend trả về. FE không tự tính. */
  discountAmount: number;
  /**
   * TCR-1298 — `voucher_type` từ `VoucherValidationResult` (vd `'free_shipping'`
   * theo enum `VoucherType` của `@tcr-erp/shared`). BE CHƯA tính được số tiền
   * giảm phí ship cho voucher nhập tay (`discountAmount` luôn 0 với loại này —
   * xem `hasFreeshipVoucher`), nên phải giữ lại type để FE tự nhận biết "có
   * freeship đang áp dụng" mà không phụ thuộc vào một con số tiền không tồn tại.
   */
  voucherType?: string;
}

interface PromotionRow {
  kind: 'promotion';
  /** TCR-1069: khoá để khớp với `applied_promotions[].promotion_id` từ BE. */
  _id?: string;
  code: string;
  name: string;
  type?: string;
  schedule?: { start_date?: string; end_date?: string };
  actions?: Array<{ discount_percent?: number; discount_amount?: number }>;
  /** TCR-1069: dùng cho cột "Khách hàng" — CTKM giới hạn nhóm/hạng nào. */
  conditions?: { customer_groups?: string[]; member_tiers?: string[] };
}

interface VoucherRow {
  kind: 'voucher';
  code: string;
  name: string;
  voucher_type: string;
  discount_amount?: number;
  discount_percent?: number;
  valid_from?: string;
  valid_until?: string;
  status?: string;
}

type PromoRow = PromotionRow | VoucherRow;

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (vouchers: VoucherItem[], giftItems: OrderItem[]) => void;
  selectedCodes?: string[];
  cartItems: OrderItem[];
  customerId?: string;
  storeId?: string;
  fulfillmentMode: FulfillmentMode;
  /**
   * TCR-1069: kết quả `/pos/promotions/evaluate` của CHÍNH giỏ hàng đang mở, do
   * `CustomerSidebar` truyền xuống (nó đã gọi sẵn cho khu vực chip khuyến mãi).
   *
   * Nhận qua prop chứ KHÔNG tự gọi hook ở đây vì hai lý do: (1) không phát sinh
   * request thứ hai cho cùng một giỏ; (2) màn Sửa đơn hàng cố tình không đánh
   * giá khuyến mãi tự động — ở đó prop này `undefined` và modal tự động lui về
   * nhãn trung tính như trước.
   */
  evaluation?: PromotionCartEvaluationDto;
  /**
   * TCR-1074 final review (Fix #2): bật vòng lặp resolve gift item (API tra
   * biến thể quà tặng theo kho ca hiện tại, mở `GiftVariantPickerDialog`, và
   * có thể CHẶN áp voucher nếu quà hết hàng). `CustomerSidebar` truyền
   * `!!onGiftItemsChange` — chỉ `true` ở `POS/index.tsx`. `Orders/edit.tsx`
   * không truyền `onGiftItemsChange` nên prop này mặc định `false`: modal chỉ
   * áp voucher (vẫn tính `discountAmount` bình thường) mà KHÔNG đụng gì tới
   * quà tặng — đúng hành vi trước khi tính năng gift-auto-add tồn tại. Thiếu
   * gate này thì áp voucher gift ở màn sửa đơn gọi nhầm API kho theo store của
   * ca đang mở (không phải store của đơn đang sửa) và có thể chặn cứng voucher
   * với lỗi "hết hàng" không liên quan gì tới đơn đang sửa.
   */
  resolveGiftItems?: boolean;
  /**
   * Hình thức thanh toán đang chọn — gửi kèm `/pos/promotions/vouchers/validate`
   * để `ConditionType.PAYMENT_METHOD` của voucher chấm đúng.
   */
  paymentMethod?: string;
}

function rowLabel(row: PromoRow): string {
  if (row.kind === 'voucher') {
    if (row.discount_percent) return `Giảm ${row.discount_percent}%`;
    if (row.discount_amount) return `Giảm ${formatVND(row.discount_amount)}`;
    return row.voucher_type;
  }
  const action = row.actions?.[0];
  if (action?.discount_percent) return `Giảm ${action.discount_percent}%`;
  if (action?.discount_amount) return `Giảm ${formatVND(action.discount_amount)}`;
  return PROMOTION_TYPE_LABEL[row.type as PromotionType] ?? '—';
}

function rowStart(row: PromoRow): string | undefined {
  return row.kind === 'voucher' ? row.valid_from : row.schedule?.start_date;
}

function rowEnd(row: PromoRow): string | undefined {
  return row.kind === 'voucher' ? row.valid_until : row.schedule?.end_date;
}

/**
 * Tag trạng thái voucher — khớp logic trang /promotions/vouchers: hết hạn theo
 * `valid_until` -> "Hết hạn" đỏ, còn lại map theo status qua bảng label/color.
 */
function voucherStatusTag(row: VoucherRow) {
  const statusKey = (row.status ?? 'draft').toLowerCase() as PromotionStatus;
  const isExpired =
    (statusKey === 'active' || statusKey === 'expired') &&
    Boolean(row.valid_until && new Date(row.valid_until).getTime() < Date.now());
  if (isExpired || statusKey === 'expired') return <Tag color="red">{PROMOTION_STATUS_LABEL.expired}</Tag>;
  return (
    <Tag color={PROMOTION_STATUS_COLOR[statusKey] || 'default'}>
      {PROMOTION_STATUS_LABEL[statusKey] ?? row.status ?? '—'}
    </Tag>
  );
}

/** true khi lỗi axios là 429 — chạm rate limit của ThrottlerGuard. */
function isRateLimited(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 429;
}

export function VoucherModal({
  open,
  onClose,
  onConfirm,
  selectedCodes,
  cartItems,
  customerId,
  storeId,
  fulfillmentMode,
  evaluation,
  resolveGiftItems = false,
  paymentMethod,
}: Props) {
  const { data: promoRes, isFetching: promoFetching } = usePosPromotionList({ limit: 100, status: 'active' });
  const { data: voucherRes, isFetching: voucherFetching } = usePosVoucherList({ limit: 100, status: 'active' });
  const validateVoucher = usePosValidateVoucher();

  // TCR-1069: chỉ để đổi id nhóm/hạng trong `conditions` thành TÊN cho cột
  // "Khách hàng". Cả hai hook đều `staleTime` dài và dùng chung cache với các
  // màn khác, nên mở modal không kéo thêm request trong thực tế.
  const { data: customerGroups = [] } = useCustomerGroups();
  const { data: loyaltyTiers = [] } = usePosLoyaltyTiers();

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of customerGroups) map.set(g.value, g.label);
    return map;
  }, [customerGroups]);

  const tierNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of loyaltyTiers) {
      map.set(t.tier_id, t.name);
    }
    return map;
  }, [loyaltyTiers]);

  /**
   * TCR-1069: tra cứu theo `promotion_id`, KHÔNG theo `code` — `code` do người
   * dùng nhập nên có thể trùng giữa CTKM và voucher, còn `_id` thì không.
   */
  const appliedById = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of evaluation?.applied_promotions ?? []) {
      map.set(p.promotion_id, p.discount_amount);
    }
    return map;
  }, [evaluation]);

  const ineligibleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of evaluation?.ineligible_promotions ?? []) {
      const first = p.reasons?.[0]?.message;
      if (first) map.set(p.promotion_id, first);
    }
    return map;
  }, [evaluation]);

  const [query, setQuery] = useState('');
  const [checked, setChecked] = useState<string[]>([]);
  /** code → kết quả validate gần nhất. Dùng cho cột Trạng thái. */
  const [results, setResults] = useState<Record<string, VoucherValidationResult>>({});

  // TCR-1074: khi voucher gift trỏ tới sản phẩm nhiều variant, mở dialog cho
  // thu ngân chọn. Tách vào `useGiftVariantPicker` (dùng chung với
  // `POS/index.tsx` — quà CTKM cũng cần đúng đường tra kho + mở picker này).
  const { request: giftPicker, pickGiftVariant, resolvePick: handleGiftPickerSelect } =
    useGiftVariantPicker();

  // TCR-1074 review fix (Critical #2): chặn double-click "Đồng ý" re-entrant
  // vào handleConfirm() trong khi 1 lần gọi khác đang chạy dở (validate/gift
  // resolution/picker) — nếu không, lần gọi thứ 2 ghi đè resolver của lần gọi
  // thứ nhất bên trong `useGiftVariantPicker`, khiến `await
  // pickGiftVariant(...)` của lần đầu treo vĩnh viễn. Dùng ref (đọc/ghi đồng
  // bộ, không qua batching của setState) làm nguồn chặn thật; state
  // `confirming` chỉ để disable UI nút "Đồng ý".
  const confirmInFlightRef = useRef(false);
  const [confirming, setConfirming] = useState(false);

  // Chỉ reset khi modal chuyển từ đóng → mở. `selectedCodes` là mảng MỚI mỗi
  // lần POSPage re-render (theo tick giá vàng), nên không thể dùng identity
  // của mảng làm dep — sẽ reset nhầm giữa lúc thu ngân đang gõ/áp mã.
  const selectedCodesKey = (selectedCodes ?? []).join(',');
  useEffect(() => {
    if (open) {
      setChecked(selectedCodes ?? []);
      setQuery('');
      setResults({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedCodesKey]);

  const rows = useMemo<PromoRow[]>(() => {
    const promotions: PromoRow[] = (promoRes?.data ?? [])
      .filter((p: { status?: string }) => p.status === 'active')
      .map((p) => ({
        ...p,
        code: p.code ?? p.promotion_id,
        _id: p.promotion_id,
        kind: 'promotion' as const,
      }));

    const vouchers: PromoRow[] = (voucherRes?.data ?? [])
      .filter((v: { status?: string }) => v.status === 'active')
      .map((v) => ({ ...v, kind: 'voucher' as const }));

    return [...vouchers, ...promotions];
  }, [promoRes, voucherRes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q),
    );
  }, [rows, query]);

  function buildPayload(code: string): ValidateVoucherPayload {
    return {
      code,
      // TCR-1272 + TCR-1150 Bug2: giá gửi đi là giá NET (đã trừ chiết khấu
      // dòng) — `buildEvaluateItems` tính đúng công thức đó, đồng thời lọc
      // dòng quà và gửi kèm flash_sale_item_id. Trước đây file này giữ một
      // bản copy tay của cùng công thức.
      items: buildEvaluateItems(cartItems),
      customer_id: customerId,
      store_id: storeId,
      channel: 'pos',
      payment_method: paymentMethod,
    };
  }

  /** Validate 1 mã. Trả null khi lỗi mạng/429 (đã báo message). */
  async function runValidate(code: string): Promise<VoucherValidationResult | null> {
    try {
      const result = await validateVoucher.mutateAsync(buildPayload(code));
      setResults((prev) => ({ ...prev, [code]: result }));
      return result;
    } catch (err: unknown) {
      if (isRateLimited(err)) {
        message.warning('Thao tác quá nhanh, thử lại sau vài giây');
      } else {
        message.error('Không kiểm tra được voucher, vui lòng thử lại');
      }
      return null;
    }
  }

  async function applyCode() {
    const code = query.trim().toUpperCase();
    if (!code) return;

    const result = await runValidate(code);
    if (!result) return; // lỗi mạng/429 — giữ nguyên voucher đang có

    if (!result.valid) {
      message.error(result.reason ?? 'Mã giảm giá không hợp lệ');
      return;
    }

    setChecked((prev) => (prev.includes(code) ? prev : [...prev, code]));
    message.success(`Đã áp dụng ${code}`);
  }

  async function handleConfirm() {
    // TCR-1074 review fix (Critical #2): no-op nếu 1 lần handleConfirm() khác
    // đang chạy dở (double-click "Đồng ý" trước khi request đầu xong).
    if (confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    setConfirming(true);
    try {
      // Lặp theo `checked` (mã đã tick/đã áp), KHÔNG theo `rows`: mã hợp lệ nhập
      // tay có thể không nằm trong `rows` (vượt quá limit:100, hoặc voucher
      // ẩn/targeted không trả trong list) — lặp theo rows sẽ âm thầm loại nó.
      const promotionCodes = new Set(
        rows.filter((r): r is PromotionRow => r.kind === 'promotion').map((r) => r.code),
      );
      const rowByCode = new Map(rows.map((r) => [r.code, r]));

      // TCR-1074 review fix (Critical #1): lưu lại NGAY kết quả validate của
      // execution này (không đọc lại từ state `results` ở vòng lặp gift bên
      // dưới) — `setResults(...)` trong `runValidate()` chỉ SCHEDULE update,
      // biến `results` mà closure này đang giữ vẫn là giá trị snapshot lúc
      // handleConfirm() được gọi nên sẽ KHÔNG thấy kết quả validate vừa xong
      // trong CHÍNH lần chạy này (chỉ thấy ở lần re-render sau). Với mã được
      // chọn qua checkbox (`rowSelection`/`selectedCodes`, chưa từng qua
      // `applyCode()`), `results[code]` lúc đó vẫn undefined dù `result` vừa
      // validate xong hợp lệ và có gift_product_id — khiến vòng lặp gift bên
      // dưới im lặng bỏ qua resolve quà tặng.
      const resultsByCode = new Map<string, VoucherValidationResult>();

      const applied: VoucherItem[] = [];
      for (const code of checked) {
        // `checked` về nguyên tắc chỉ chứa voucher (checkbox CTKM bị disable),
        // nhưng bỏ qua CTKM để chắc chắn.
        if (promotionCodes.has(code)) continue;

        const cached = results[code];
        const result = cached?.valid ? cached : await runValidate(code);
        if (!result) return; // lỗi mạng/429 — không đóng modal
        if (!result.valid) {
          message.error(`${code}: ${result.reason ?? 'Không hợp lệ'}`);
          return;
        }
        resultsByCode.set(code, result);

        const row = rowByCode.get(code);
        // Mã nhập tay (ô "Nhập mã giảm giá") có thể không nằm trong `rows`
        // (xem comment ở khai báo `rowByCode` phía trên) — trước đây fallback
        // thẳng về `code`, khiến chip hiện "CODE - CODE". `runValidate()` giờ
        // trả kèm `voucher_name` từ chính response validate của BE nên dùng
        // được làm nguồn thứ 2 trước khi đành fallback về code.
        const label = row?.kind === 'voucher' ? row.name : (result.voucher_name ?? code);
        applied.push({
          code,
          label,
          description: row ? rowLabel(row) : undefined,
          discountAmount: result.discount_amount ?? 0,
          voucherType: result.voucher_type,
        });
      }

      // TCR-1074 final review (Fix #2): `resolveGiftItems === false` nghĩa là
      // caller (`Orders/edit.tsx` qua `CustomerSidebar`) không nhận
      // `onGiftItemsChange` — bỏ qua HOÀN TOÀN vòng resolve gift bên dưới
      // (không gọi API tra kho, không mở picker, không thể chặn "hết hàng").
      // Voucher vẫn áp bình thường (`applied` đã tính xong ở trên), chỉ khác
      // là không đụng gì tới quà tặng — đúng hành vi màn sửa đơn trước khi
      // tính năng gift-auto-add tồn tại.
      let giftItems: OrderItem[] = [];
      if (resolveGiftItems) {
        // TCR-1074: giữ nguyên gift item đã có trong giỏ cho voucher vẫn còn được
        // chọn (không fetch/prompt lại), chỉ resolve mới cho voucher gift VỪA
        // được thêm vào `checked` mà chưa có dòng gift tương ứng trong cartItems.
        const currentGiftItems = cartItems.filter((i) => i.is_gift && i.gift_promotion_id);
        const appliedCodes = new Set(applied.map((v) => v.code));
        const keepGiftItems = currentGiftItems.filter(
          (gi) => gi.gift_promotion_id && appliedCodes.has(gi.gift_promotion_id),
        );
        const keptCodes = new Set(keepGiftItems.map((gi) => gi.gift_promotion_id));

        const newGiftItems: OrderItem[] = [];
        for (const code of checked) {
          if (promotionCodes.has(code)) continue;
          if (keptCodes.has(code)) continue;

          const result = resultsByCode.get(code);
          if (!result?.valid || !result.gift_product_id || !result.gift_quantity) continue;

          const giftProductId = result.gift_product_id;
          const giftQuantity = result.gift_quantity;

          let options: PosGiftVariantOption[];
          try {
            options = await posService.listVariantsByProduct(giftProductId, storeId);
          } catch {
            message.error(`Không tải được sản phẩm quà tặng cho ${code}, vui lòng thử lại`);
            return;
          }

          const eligible = options.filter((o) => o.is_sellable && o.stock_quantity >= giftQuantity);
          if (eligible.length === 0) {
            message.error(`Sản phẩm quà tặng của ${code} hiện hết hàng, không thể áp dụng voucher`);
            return;
          }

          let chosen: PosGiftVariantOption;
          if (options.length === 1) {
            chosen = options[0];
          } else {
            const picked = await pickGiftVariant(result.gift_description ?? code, giftQuantity, options);
            if (!picked) {
              message.info(`Đã huỷ áp dụng voucher ${code} — chưa chọn biến thể quà tặng`);
              return;
            }
            chosen = picked;
          }

          const { items: detailItems, item_errors } = await posService.lookupVariants(
            [chosen.variant_code],
            fulfillmentMode,
          );
          if (item_errors.length > 0 || detailItems.length === 0) {
            message.error(`Không tải được chi tiết sản phẩm quà tặng cho ${code}`);
            return;
          }

          newGiftItems.push(
            buildGiftOrderItem(
              mapDetailToPosVariant(detailItems[0]),
              giftQuantity,
              code,
              fulfillmentMode,
            ),
          );
        }

        giftItems = [...keepGiftItems, ...newGiftItems];
      }

      onConfirm(applied, giftItems);
    } finally {
      confirmInFlightRef.current = false;
      setConfirming(false);
    }
  }

  const columns: ColumnsType<PromoRow> = [
    {
      title: 'Tên CTKM',
      key: 'code',
      render: (_: unknown, r: PromoRow) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.code}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.name}</div>
        </div>
      ),
    },
    {
      // TCR-1069: trước đây cột này render cứng 'Tất cả KH' cho mọi dòng — kể
      // cả CTKM chỉ dành cho một nhóm khách. Tester đọc đúng chữ đó rồi kết
      // luận đơn của mình đủ điều kiện.
      title: 'Khách hàng',
      key: 'customer',
      render: (_: unknown, r: PromoRow) => {
        if (r.kind !== 'promotion') return 'Tất cả KH';
        const groups = (r.conditions?.customer_groups ?? []).map(
          (id) => groupNameById.get(id) ?? 'Nhóm KH',
        );
        const tiers = (r.conditions?.member_tiers ?? []).map(
          (id) => tierNameById.get(id) ?? 'Hạng TV',
        );
        const parts = [...groups, ...tiers];
        return parts.length > 0 ? parts.join(', ') : 'Tất cả KH';
      },
    },
    { title: 'Ngày bắt đầu', key: 'startAt', render: (_: unknown, r: PromoRow) => formatDate(rowStart(r) ?? '') },
    { title: 'Ngày kết thúc', key: 'endAt', render: (_: unknown, r: PromoRow) => formatDate(rowEnd(r) ?? '') },
    { title: 'Loại khuyến mãi', key: 'type', render: (_: unknown, r: PromoRow) => rowLabel(r) },
    {
      title: 'Trạng thái',
      key: 'status',
      render: (_: unknown, r: PromoRow) => {
        if (r.kind === 'promotion') {
          // TCR-1069: chỉ nói "Tự động áp dụng" khi CHƯA biết gì (giỏ rỗng,
          // đang tải, hoặc màn Sửa đơn hàng không đánh giá). Có kết quả rồi thì
          // nói thẳng đang áp được hay chưa và thiếu điều kiện gì — nhãn xanh
          // vô điều kiện chính là thứ khiến ticket này bị báo lại lần hai.
          const discount = r._id ? appliedById.get(r._id) : undefined;
          if (discount !== undefined) {
            return <Tag color="green">Đang áp dụng · -{formatVND(discount)}</Tag>;
          }
          const reason = r._id ? ineligibleById.get(r._id) : undefined;
          if (reason) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                <Tag color="orange">Chưa đủ điều kiện</Tag>
                <span style={{ fontSize: 12, color: '#888' }}>{reason}</span>
              </div>
            );
          }
          return <Tag color="blue">Tự động áp dụng</Tag>;
        }
        const statusEl = voucherStatusTag(r);
        const result = results[r.code];
        // Chưa validate: chỉ hiện trạng thái (khớp trang /promotions/vouchers).
        if (!result) return statusEl;
        // Đã validate: kèm kết quả áp mã ngay dưới trạng thái.
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            {statusEl}
            {result.valid ? (
              <Tag color="green">Giảm {formatVND(result.discount_amount ?? 0)}</Tag>
            ) : (
              <Tag color="orange">{result.reason ?? 'Không hợp lệ'}</Tag>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="min(1080px, 94vw)"
      destroyOnClose
      title={
        <span>
          <GiftOutlined style={{ color: '#a4193d', marginRight: 8 }} />
          KHUYẾN MÃI &amp; VOUCHER
        </span>
      }
      /* TCR-1695 — bỏ nút "Thêm khuyến mãi". Nó mở tab mới sang màn TẠO chương trình khuyến
         mãi (`ROUTES.promotions.new`) — việc của marketing, không phải của thu ngân đang đứng
         giữa một đơn hàng dở; thu ngân cũng thường không có quyền vào màn đó nên bấm vào chỉ
         ra trang 403. Footer còn Đóng/Đồng ý, `justifyContent` đổi sang `flex-end` vì
         `space-between` nay chỉ còn một cụm nút. */
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={onClose}>Đóng</Button>
            <Button
              type="primary"
              loading={validateVoucher.isPending || confirming}
              disabled={confirming}
              onClick={() => void handleConfirm()}
            >
              Đồng ý
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Input
          placeholder="Nhập mã giảm giá"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={() => void applyCode()}
          allowClear
        />
        <Button type="primary" loading={validateVoucher.isPending} onClick={() => void applyCode()}>
          Áp dụng
        </Button>
      </div>

      <Table<PromoRow>
        className="voucher-ctkm-table"
        rowKey="code"
        size="small"
        columns={columns}
        dataSource={filtered}
        loading={promoFetching || voucherFetching}
        pagination={false}
        scroll={{ x: 'max-content', y: 360 }}
        locale={{ emptyText: <Empty description="Chưa có chương trình khuyến mãi nào" /> }}
        rowSelection={{
          selectedRowKeys: checked,
          preserveSelectedRowKeys: true,
          onChange: (keys) => setChecked(keys as string[]),
          getCheckboxProps: (r: PromoRow) => ({ disabled: r.kind === 'promotion' }),
        }}
      />

      {giftPicker && (
        <GiftVariantPickerDialog
          open
          onClose={() => handleGiftPickerSelect(null)}
          productName={giftPicker.productName}
          requiredQty={giftPicker.requiredQty}
          options={giftPicker.options}
          onSelect={(option) => handleGiftPickerSelect(option)}
        />
      )}
    </Modal>
  );
}
